"use client";
import React from "react";
import { motion } from "framer-motion";
import { Caveat } from "next/font/google";
import Image from "next/image";

const caveat = Caveat({ subsets: ["latin"], weight: ["400", "700"], display: "swap" });

/* ─────────────────────────────────────────────
   1. Effortlessly convert leads into opportunities
   ───────────────────────────────────────────── */
export function PhotographyLeadConversion({ section }) {
  return (
    <section className="overflow-visible py-20 bg-transparent text-white relative font-sans w-full z-10">
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] relative z-10">
        <div className="row align-items-start justify-content-between g-5">

          {/* Left: Heading */}
          <div className="col-lg-7 pb-5">
            <h2 className={`text-4xl md:text-5xl lg:text-7xl font-bold text-white leading-tight mb-0 ${caveat.className}`}>
              <span className="x_wd_blue_highlight_02">Effortlessly</span> convert leads into opportunities
            </h2>
          </div>

          {/* Left: Background Mockup (Top) */}
          <div className="col-lg-7 position-relative">
            <Image
              src="/odoo/arrows/arrow_sm_02.svg"
              width={35}
              height={35}
              className="d-none d-lg-block position-absolute top-0 start-100 mt-n5 ms-4 flip rotate-250 select-none pointer-events-none"
              style={{ filter: "brightness(0) invert(1)", opacity: 0.4 }}
              alt=""
            />
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.5)] overflow-hidden mb-10 bg-[#151517]"
            >
              <Image
                src="/odoo/industries/photography/crm-2.webp"
                width={700}
                height={450}
                className="w-full h-auto object-contain"
                alt="CRM Kanban view"
              />
            </motion.div>
          </div>

          {/* Right: Foreground Mockup & Text */}
          <div className="col-lg-4">
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="rounded-2xl shadow-[0_25px_60px_rgba(0,0,0,0.6)] overflow-hidden mb-10 mx-auto bg-[#151517] w-3/4 lg:w-full"
            >
              <Image
                src="/odoo/industries/photography/crm.webp"
                width={400}
                height={550}
                className="w-full h-auto object-contain"
                alt="CRM lead details"
              />
            </motion.div>
            <p className="text-gray-300 leading-relaxed mb-0 text-xl">
              <strong className="text-white">Clients can contact you through your website</strong> using the contact form or by requesting a quote, or they can use a dedicated email alias. Odoo automatically creates an opportunity in your CRM, capturing all the information they provide.
            </p>
          </div>

        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   2. Stunning quotations in a click
   ───────────────────────────────────────────── */
export function PhotographyQuotations({ section }) {
  return (
    <section className="overflow-visible py-20 bg-transparent text-white relative font-sans w-full z-10">
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] relative z-10">
        
        {/* Top: Title and descriptions */}
        <div className="row justify-content-center mb-16">
          <div className="col-lg-11 offset-xl-1 pb-5 position-relative">
            <h2 className={`text-4xl md:text-5xl lg:text-7xl font-bold text-white leading-tight mb-0 relative z-10 ${caveat.className}`}>
              Stunning quotations in a click
            </h2>
          </div>
          <div className="col-lg-6 col-xl-5">
            <p className="text-gray-300 leading-relaxed mb-8 text-xl">
              <strong className="text-white">PDF quote builder.</strong> Send fully customized PDF quotes, showcasing your work and vision, with various information and design elements.
            </p>
          </div>
          <div className="col-lg-6 col-xl-5">
            <p className="text-gray-300 leading-relaxed mb-8 text-xl">
              <strong className="text-white">Quotation templates.</strong> Create reusable quotation templates for standard products or service requests, adapt them effortlessly, and send them to your clients much faster.
            </p>
          </div>
        </div>

        {/* Bottom: Dynamic collage of quotations */}
        <div className="relative py-12">
          
          {/* Heart Icon Decorator */}
          <Image
            src="/odoo/icons/red_heart.svg"
            width={70}
            height={70}
            className="absolute top-0 left-4 lg:left-12 -translate-y-1/2 select-none pointer-events-none z-20"
            style={{ height: 70, width: "auto", transform: "rotate(350deg)" }}
            alt=""
          />

          <div className="row justify-content-center relative">
            
            {/* Left Block (Collage 1) */}
            <div className="col-lg-6 flex justify-center lg:justify-end mx-auto py-4 py-lg-0 relative z-10">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="w-[60%] rounded-xl shadow-2xl z-20 mt-14 bg-[#151517] -mr-[35%] overflow-hidden relative"
              >
                <Image
                  src="/odoo/industries/photography/quotation-1.webp"
                  width={400}
                  height={300}
                  className="w-full h-auto object-cover"
                  alt="Quotation layout 1"
                />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="w-[60%] rounded-xl shadow-lg z-10 mb-14 bg-[#151517] overflow-hidden relative"
              >
                <Image
                  src="/odoo/industries/photography/quotation-2.webp"
                  width={400}
                  height={300}
                  className="w-full h-auto object-cover"
                  alt="Quotation layout 2"
                />
              </motion.div>
            </div>

            {/* Right Block (Collage 2) */}
            <div className="col-lg-6 flex justify-center lg:justify-start mx-auto py-4 py-lg-0 relative z-10">
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="w-[60%] rounded-xl shadow-lg z-10 mb-14 bg-[#151517] overflow-hidden relative"
              >
                <Image
                  src="/odoo/industries/photography/quotation-3.webp"
                  width={400}
                  height={300}
                  className="w-full h-auto object-cover"
                  alt="Quotation layout 3"
                />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="w-[60%] rounded-xl shadow-2xl z-30 mt-14 bg-[#151517] -ml-[35%] overflow-hidden relative"
              >
                <Image
                  src="/odoo/industries/photography/quotation-4.webp"
                  width={400}
                  height={300}
                  className="w-full h-auto object-cover"
                  alt="Quotation layout 4"
                />
              </motion.div>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   3. Less scheduling, more shooting
   ───────────────────────────────────────────── */
export function PhotographyScheduling({ section }) {
  return (
    <section className="overflow-visible py-20 bg-transparent text-white relative font-sans w-full z-10">
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] relative z-10">
        
        {/* Title */}
        <div className="text-center mb-16">
          <h2 className={`text-4xl md:text-5xl lg:text-7xl font-bold text-white leading-tight ${caveat.className}`}>
            Less scheduling, more{" "}
            <span className="x_wd_yellow_highlight_bold_04 relative inline-block">
              shooting
              {/* Crown Icon Decorator */}
              <Image
                src="/odoo/icons/crown.svg"
                width={100}
                height={100}
                style={{ height: 100, width: "auto", transform: "rotate(20deg)" }}
                className="hidden lg:block absolute -top-8 -right-16 select-none pointer-events-none"
                alt=""
              />
            </span>
          </h2>
        </div>

        {/* 3-Column Explanations */}
        <div className="row justify-content-center mb-16 g-5">
          <div className="col-lg-4">
            <p className="mb-0 text-xl text-gray-300 leading-relaxed">
              <strong className="text-white">Online appointments.</strong> Create different appointment types that clients can book based on your availability.
            </p>
          </div>
          <div className="col-lg-4">
            <p className="mb-0 text-xl text-gray-300 leading-relaxed">
              <strong className="text-white">Customer portal.</strong> Clients can manage or reschedule their appointments from dedicated customer portal — effortlessly.
            </p>
          </div>
          <div className="col-lg-4">
            <p className="mb-0 text-xl text-gray-300 leading-relaxed">
              <strong className="text-white">Calendar view.</strong> Enjoy a calendar that synchronizes with Google and Outlook to keep your projects organized.
            </p>
          </div>
        </div>

        {/* Overlapping Mockups */}
        <div className="row align-items-center justify-content-center">
          
          <div className="col-md-7 position-relative mb-10 md:mb-0">
            {/* Tap icon pointing */}
            <Image
              src="/odoo/icons/tap-1.svg"
              width={90}
              height={90}
              className="hidden lg:block absolute -bottom-10 left-1/2 select-none pointer-events-none z-30"
              style={{ height: 90, width: "auto", transform: "rotate(320deg) translateX(40px)" }}
              alt=""
            />
            {/* Curved arrow pointing to mobile mockup */}
            <Image
              src="/odoo/arrows/arrow_sm_02.svg"
              width={35}
              height={35}
              className="hidden lg:block absolute top-1/2 -right-8 select-none pointer-events-none"
              style={{ filter: "brightness(0) invert(1)", opacity: 0.3, transform: "rotate(300deg)" }}
              alt=""
            />
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.5)] overflow-hidden bg-[#151517]"
            >
              <Image
                src="/odoo/industries/photography/appointment.webp"
                width={750}
                height={500}
                className="w-full h-auto object-contain"
                alt="Calendar appointment view"
              />
            </motion.div>
          </div>

          <div className="col-md-3">
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="rounded-2xl shadow-[0_25px_60px_rgba(0,0,0,0.6)] overflow-hidden mx-auto bg-[#151517] w-3/4 md:w-full"
            >
              <Image
                src="/odoo/industries/photography/select-date-time.webp"
                width={300}
                height={500}
                className="w-full h-auto object-contain"
                alt="Mobile select date time"
              />
            </motion.div>
          </div>

        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   4. Smart project management
   ───────────────────────────────────────────── */
export function PhotographyProjectManagement({ section }) {
  return (
    <section className="overflow-visible py-20 bg-transparent text-white relative font-sans w-full z-10">
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] relative z-10">
        <div className="row align-items-center justify-content-between g-5">

          {/* Left (Text) */}
          <div className="col-lg-5 order-lg-last pb-5 pb-lg-0 relative">
            {/* Green Tick Icon */}
            <div className="absolute -top-12 right-0 hidden lg:block">
              <Image
                src="/odoo/icons/green_tick-2.svg"
                width={70}
                height={70}
                className="w-[70px] select-none pointer-events-none"
                style={{ transform: "rotate(20deg)" }}
                alt=""
              />
            </div>
            
            <h2 className={`text-4xl md:text-5xl lg:text-7xl font-bold text-white leading-tight mb-6 ${caveat.className}`}>
              <span className="x_wd_green_highlight_02">Smart</span> project management
            </h2>
            <p className="text-gray-300 leading-relaxed mb-0 text-xl">
              <strong className="text-white">Once your quote is confirmed, a dedicated project is created</strong> for your photo shoot, allowing you to stay organized with a visual dashboard, a customizable pipeline, tags, priorities, allocated time, and more.
            </p>
          </div>

          {/* Right (Mockup) */}
          <div className="col-lg-6 relative py-5 py-lg-7">
            {/* Background shape */}
            <div className="absolute inset-0 bg-white/[0.03] rounded-3xl -z-10" />

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.5)] overflow-hidden bg-[#151517]"
            >
              <Image
                src="/odoo/industries/photography/project.webp"
                width={800}
                height={500}
                className="w-full h-auto object-contain"
                alt="Project management kanban"
              />
            </motion.div>
          </div>

        </div>
      </div>
    </section>
  );
}
